
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import CancelButton from "../buttons/CancelButton.vue";
import DangerButton from "../buttons/DangerButton.vue";
import {
  getDeviceTypes,
  getDeviceProperties,
  createDeviceType,
  createDeviceProperty,
  deleteDeviceType,
  deleteDeviceProperty,
} from "@/api/stations";
import { showToast } from "@/utils/toastService";
import { getAxiosResponse } from "@/utils/errorUtils";

type ManagedKind = "types" | "properties";
type Row = { name: string };

@Component({
  components: {
    DangerButton,
    CancelButton,
  },
})
export default class StringListAdmin extends Vue {
  @Prop({ default: "types" }) readonly kind!: ManagedKind;

  loading = false;
  items: string[] = [];

  addDialog = false;
  newValue = "";

  // delete dialog
  deleteDialog: { open: boolean; value: string | null } = {
    open: false,
    value: null,
  };

  get title() {
    return this.kind === "types" ? "Boxtypen" : "Eigenschaften";
  }

  get icon() {
    return this.kind === "types" ? "mdi-tag-text-outline" : "mdi-tag-multiple-outline"
  }

  get dialogHeadline() {
    return this.kind === "types"
      ? "Neuen Boxtyp hinzufügen"
      : "Neue Eigenschaft hinzufügen";
  }

  get labelSingular() {
    return this.kind === "types" ? "Boxtyp" : "Eigenschaft";
  }

  get headers() {
    return [
      { text: "Name", value: "name" },
      { text: "Aktion", value: "actions", align: "end", sortable: false },
    ];
  }

  get rows(): Row[] {
    return this.items.map((n) => ({ name: n }));
  }

  async mounted() {
    await this.load();
  }

  openAddDialog() {
    this.newValue = "";
    this.addDialog = true;
  }

  confirmDelete(value: string) {
    this.deleteDialog = { open: true, value };
  }

  async load() {
    this.loading = true;
    try {
      this.items =
        this.kind === "types"
          ? await getDeviceTypes()
          : await getDeviceProperties();
    } catch (e) {
      this.items = [];
      this.$emit("load-error", e);
    } finally {
      this.loading = false;
    }
  }

  async create() {
    const value = this.newValue;
    if (!value) return;
    this.loading = true;
    try {
      if (this.kind === "types") {
        await createDeviceType(value);
        showToast({
          message: "Gerätetyp wurde erfolgreich erstellt",
          type: "success",
        });
      } else {
        await createDeviceProperty(value);
        showToast({
          message: "Geräteeigenschaft wurde erfolgreich erstellt",
          type: "success",
        });
      }
      this.addDialog = false;
      await this.load();
    } catch (e: any) {
      console.error("Create error:", e);
      console.error("getAxiosResponse(e):", getAxiosResponse(e));

      if (this.kind === "types") {
        showToast({
          response: getAxiosResponse(e),
          message: "Gerätetyp konnte nicht erstellt werden",
          type: "error",
        });
      } else {
        showToast({
          response: getAxiosResponse(e),
          message: "Geräteeigenschaft konnte nicht erstellt werden",
          type: "error",
        });
      }
      this.$emit("create-error", e);
    } finally {
      this.loading = false;
    }
  }

  async remove() {
    const value = this.deleteDialog.value;
    if (!value) return;
    this.loading = true;
    try {
      if (this.kind === "types") {
        await deleteDeviceType(value);
        showToast({
          message: "Gerätetyp wurde erfolgreich gelöscht",
          type: "success",
        });
      } else {
        await deleteDeviceProperty(value);
        showToast({
          message: "Geräteeigenschaft wurde erfolgreich gelöscht",
          type: "success",
        });
      }
      this.deleteDialog = { open: false, value: null };
      await this.load();
    } catch (e: any) {
      console.error("Delete error:", e);
      if (this.kind === "types") {
        showToast({
          response: getAxiosResponse(e),
          message: "Gerätetyp konnte nicht gelöscht werden",
          type: "error",
        });
      } else {
        showToast({
          response: getAxiosResponse(e),
          message: "Geräteeigenschaft konnte nicht gelöscht werden",
          type: "error",
        });
      }
      this.$emit("delete-error", e);
    } finally {
      this.loading = false;
    }
  }
}
